import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";
import { SectionTitle } from "../analytics/AnalyticsShared";
import { AgentGraph } from "./AgentGraph";
import { AgentDetailSheet } from "./AgentDetailSheet";
import { RecentTasks } from "./RecentTasks";
import { AgentAlerts } from "./AgentAlerts";
import { EMPTY_FILTERS } from "./TaskFilters";
import { STATUS_META, TaskStatusBadge, fmtConf, fmtMs } from "./agentShared";

const MANUAL = { "costing.estimate": '{"process": "CNC Machining", "material": "Aluminum", "quantity": 10}', "material.resolve": '{"designation": "SS316L"}', "partner.match": '{"order_id": ""}', "quality.checklist": '{"order_id": ""}', "manufacturing.next_steps": '{"order_id": ""}', "engineering.evaluate": '{"rfq_id": ""}', "validation.narrative": '{"text": "Rework rate is 50% this week.", "allowed": ["50", "0.5"]}' };

const Console = ({ onRan }) => {
  const [type, setType] = useState("costing.estimate");
  const [payload, setPayload] = useState(MANUAL["costing.estimate"]);
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    let body;
    try { body = JSON.parse(payload || "{}"); } catch { toast.error("Payload must be valid JSON."); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/brain/tasks", { task_type: type, payload: body });
      setOut(data);
      onRan();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="brain-console">
      <p className="text-xs text-muted-foreground">Send a task to the Brain. It classifies, selects the agent, enforces rules, checks memory, executes and validates — then returns the structured result contract.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={type} onValueChange={(v) => { setType(v); setPayload(MANUAL[v]); setOut(null); }}>
          <SelectTrigger className="sm:w-60" data-testid="console-task-type"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(MANUAL).map((k) => <SelectItem key={k} value={k} data-testid={`console-task-${k.replace(".", "-")}`}>{k}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea value={payload} onChange={(e) => setPayload(e.target.value)} rows={2} className="font-mono-tech text-xs flex-1" data-testid="console-payload" />
        <Button onClick={run} disabled={busy} className="rounded-full sm:self-start" data-testid="console-run-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}Run via Brain</Button>
      </div>
      {out && (
        <div className="rounded-lg bg-muted/40 p-3 text-xs space-y-2" data-testid="console-result">
          <div className="flex flex-wrap items-center gap-2"><TaskStatusBadge status={out.status} testId="console-result-status" /><span className="font-mono-tech" data-testid="console-result-agent">{out.agent_id}</span><span>conf {fmtConf(out.confidence)}</span><span>{fmtMs(out.processing_ms)}</span>{out.memory_hit && <span className="text-primary">memory hit</span>}{out.dedup === "collapsed" && <span className="text-primary" data-testid="console-result-collapsed">collapsed</span>}{out.requires_review && <span className="text-amber-600">review required</span>}{out.error && <span className="text-red-600" data-testid="console-result-error">{out.error}</span>}</div>
          {out.result && <pre className="max-h-48 overflow-auto font-mono-tech whitespace-pre-wrap break-all" data-testid="console-result-json">{JSON.stringify(out.result, null, 1)}</pre>}
        </div>
      )}
    </div>
  );
};

export const AgentControlCenter = () => {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [taskFilters, setTaskFilters] = useState(EMPTY_FILTERS);
  const investigate = (a) => {
    setTaskFilters({ ...EMPTY_FILTERS, agent_id: a.agent_id, status: a.status === "ERROR" ? "failed" : "rejected" });
    document.getElementById("agent-tasks-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = useCallback(async () => {
    try {
      const { data: a } = await adminApi.get("/admin/brain/agents");
      setData(a);
      setTick((n) => n + 1);
      setError("");
    } catch {
      setError("Could not load the Agent Control Center.");
    }
  }, []);
  useEffect(() => {
    load();
    const poll = () => { if (!document.hidden) load(); };  // no polling while the tab is hidden
    const id = setInterval(poll, 15000);
    document.addEventListener("visibilitychange", poll);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", poll); };
  }, [load]);

  if (error) return <p className="text-sm text-destructive py-6" data-testid="agents-error">{error}</p>;
  if (!data) return <div className="py-16 flex justify-center" data-testid="agents-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={1.5} /></div>;
  const counts = data.agents.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="agent-control-center">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">Frontend → Vectron Brain → Task Router → Specialized Agent → Agent Memory / Knowledge Core → Brain validation → Frontend. Manufacturing facts always come from the Unified Manufacturing Library; agent memory only references them.</p>
        <div className="flex flex-wrap items-center gap-2" data-testid="agent-status-legend">
          {Object.entries(STATUS_META).map(([k, m]) => <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid={`legend-${k}`}><span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label} {counts[k] ? `(${counts[k]})` : ""}</span>)}
          <Button size="icon" variant="ghost" onClick={load} className="h-7 w-7" data-testid="agents-refresh-btn" aria-label="Refresh"><RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
        </div>
      </div>
      <AgentAlerts agents={data.agents} onInvestigate={investigate} onOpen={setOpen} />
      <AgentGraph brain={data.brain} agents={data.agents} onOpen={setOpen} />
      <SectionTitle>Brain console</SectionTitle>
      <Console onRan={load} />
      <SectionTitle>Agent task investigation</SectionTitle>
      <RecentTasks tick={tick} onOpenAgent={setOpen} filters={taskFilters} onFiltersChange={setTaskFilters} />
      <AgentDetailSheet agentId={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
};
